import { Fragment, useState, useEffect } from 'react'
import { useAuth } from '../lib/AuthContext'
import { supabase, getUserCompanyId } from '../lib/supabase'

// ---------- status palette ----------
const STATUS = {
  green:  { label: 'On track',   color: '#166534', bg: '#F0FDF4', border: '#86EFAC', stroke: '#22A24A' },
  yellow: { label: 'Watch',      color: '#854D0E', bg: '#FEFCE8', border: '#FDE047', stroke: '#C4912A' },
  red:    { label: 'Off track',  color: '#991B1B', bg: '#FEF2F2', border: '#FCA5A5', stroke: '#B04545' },
  empty:  { label: 'Needs goal', color: '#666',    bg: '#ECECEC', border: '#D4D4D4', stroke: '#999'    },
}

const DEFAULT_SCORECARD = {
  metrics: [
    { id: 1, name: 'Revenue',         owner: '',  goal: '',  cadence: 'weekly',  direction: 'higher', entries: {} },
    { id: 2, name: 'New customers',   owner: '',  goal: '',  cadence: 'weekly',  direction: 'higher', entries: {} },
    { id: 3, name: 'Customer churn',  owner: '',  goal: '',  cadence: 'monthly', direction: 'lower',  entries: {} },
  ]
}

// ---------- numeric helpers ----------
// Parse "50k", "$12,000", "60%", "1.2M" → number; "" or unparseable → NaN
function parseNumeric(str) {
  if (str === null || str === undefined) return NaN
  let s = String(str).trim().replace(/[$,\s]/g, '')
  if (!s) return NaN
  let mult = 1
  if (/k$/i.test(s))      { mult = 1_000;     s = s.slice(0, -1) }
  else if (/m$/i.test(s)) { mult = 1_000_000; s = s.slice(0, -1) }
  else if (/%$/.test(s))  {                   s = s.slice(0, -1) }
  const n = parseFloat(s)
  return isNaN(n) ? NaN : n * mult
}

// Derive status from goal vs actual. direction = 'higher' (default) means actual >= goal is good.
function deriveStatus(goalStr, actualStr, direction = 'higher', tolerance = 0.1) {
  const g = parseNumeric(goalStr)
  const a = parseNumeric(actualStr)
  if (isNaN(g)) return 'empty'
  if (isNaN(a)) return 'empty'
  if (direction === 'lower') {
    if (a <= g) return 'green'
    if (a <= g * (1 + tolerance)) return 'yellow'
    return 'red'
  }
  if (a >= g) return 'green'
  if (a >= g * (1 - tolerance)) return 'yellow'
  return 'red'
}

// ---------- date helpers ----------
function getQuarterOptions() {
  const now = new Date()
  const year = now.getFullYear()
  const q = Math.ceil((now.getMonth() + 1) / 3)
  const options = []
  for (let i = -1; i <= 1; i++) {
    let qn = q + i, yn = year
    while (qn < 1) { qn += 4; yn-- }
    while (qn > 4) { qn -= 4; yn++ }
    options.push(`Q${qn} ${yn}`)
  }
  return options
}

function getCurrentQuarter() {
  const now = new Date()
  return `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}`
}

function getWeeksForQuarter(quarterStr) {
  const [q, year] = quarterStr.split(' ')
  const qNum = parseInt(q.replace('Q', ''))
  const yr = parseInt(year)
  const startMonth = (qNum - 1) * 3
  const qStart = new Date(yr, startMonth, 1)
  const qEnd = new Date(yr, startMonth + 3, 0)
  const now = new Date()
  const end = qEnd < now ? qEnd : now

  const weeks = []
  const d = new Date(qStart)
  const day = d.getDay()
  const diff = day === 0 ? 1 : day === 1 ? 0 : 8 - day
  d.setDate(d.getDate() + diff)

  while (d <= end) {
    const key = d.toISOString().split('T')[0]
    const label = `${d.getMonth() + 1}/${d.getDate()}`
    weeks.push({ key, label })
    d.setDate(d.getDate() + 7)
  }
  return weeks.slice(-13)
}

// All entries for a metric (across history), sorted oldest → newest, only those with a numeric actual.
function getNumericEntriesSorted(metric) {
  if (!metric?.entries) return []
  return Object.entries(metric.entries)
    .map(([key, e]) => ({ key, actual: parseNumeric(e?.actual) }))
    .filter(e => !isNaN(e.actual))
    .sort((a, b) => a.key.localeCompare(b.key))
}

// Latest entry within (or up to) the given week keys; returns the most recent week with a value, or null.
function getLatestEntryWithin(metric, weekKeys) {
  if (!metric?.entries) return null
  const set = new Set(weekKeys)
  // Walk newest → oldest within the visible window
  for (let i = weekKeys.length - 1; i >= 0; i--) {
    const k = weekKeys[i]
    const e = metric.entries[k]
    if (e && (e.actual !== '' && e.actual !== undefined && e.actual !== null)) {
      return { key: k, actual: e.actual }
    }
  }
  return null
}

// Count consecutive 'red' periods at the end of the metric's history (within visible window).
function countTrailingMisses(metric, weekKeys) {
  let count = 0
  for (let i = weekKeys.length - 1; i >= 0; i--) {
    const k = weekKeys[i]
    const e = metric.entries?.[k]
    if (!e || e.actual === '' || e.actual === undefined) continue // skip empty
    const s = deriveStatus(metric.goal, e.actual, metric.direction)
    if (s === 'red') count++
    else if (s === 'green' || s === 'yellow') break
  }
  return count
}

// ---------- supabase ----------
let nid = 100

async function loadScorecard(companyId) {
  if (!companyId) return null
  const { data } = await supabase
    .from('scorecards')
    .select('*')
    .eq('company_id', companyId)
    .maybeSingle()
  return data
}

async function saveScorecard(companyId, data) {
  if (!companyId) return
  const { data: { session } } = await supabase.auth.getSession()
  const userId = session?.user?.id
  await supabase
    .from('scorecards')
    .upsert(
      { company_id: companyId, user_id: userId, scorecard_data: data, updated_at: new Date().toISOString() },
      { onConflict: 'company_id' }
    )
}

// ---------- sparkline ----------
function Sparkline({ values, status, width = 100, height = 28 }) {
  if (!values || values.length < 2) {
    return <div style={{ fontSize: 10, color: '#bbb', fontStyle: 'italic' }}>—</div>
  }
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const stepX = width / (values.length - 1)
  const path = values
    .map((v, i) => {
      const x = i * stepX
      const y = height - ((v - min) / range) * (height - 4) - 2
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
  const stroke = STATUS[status]?.stroke || '#999'
  const lastX = (values.length - 1) * stepX
  const lastY = height - ((values[values.length - 1] - min) / range) * (height - 4) - 2
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r="2.2" fill={stroke} />
    </svg>
  )
}

// ---------- main component ----------
export default function Scorecard({ previewUserId }) {
  const { user: authUser } = useAuth()
  const user = previewUserId ? { ...authUser, id: previewUserId } : authUser
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState('saved')
  const [quarter, setQuarter] = useState(getCurrentQuarter())
  const [editingMeta, setEditingMeta] = useState(null)
  const [editingLatest, setEditingLatest] = useState(null) // metric.id when editing latest
  const [latestDraft, setLatestDraft] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [dbReady, setDbReady] = useState(true)
  const [companyId, setCompanyId] = useState(previewUserId || null)
  const [companyIdResolved, setCompanyIdResolved] = useState(!!previewUserId)

  // Mobile detection
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  useEffect(() => {
    if (previewUserId) { setCompanyId(previewUserId); setCompanyIdResolved(true); return }
    if (!authUser?.email) return
    let alive = true
    getUserCompanyId(authUser.email).then(cid => { if (!alive) return; setCompanyId(cid); setCompanyIdResolved(true) })
    return () => { alive = false }
  }, [authUser?.email, previewUserId])

  useEffect(() => {
    async function load() {
      if (!user || !companyIdResolved) return
      try {
        const result = await loadScorecard(companyId)
        if (result?.scorecard_data) setData(result.scorecard_data)
        else setData(JSON.parse(JSON.stringify(DEFAULT_SCORECARD)))
      } catch {
        setData(JSON.parse(JSON.stringify(DEFAULT_SCORECARD)))
        setDbReady(false)
      }
      setLoading(false)
    }
    load()
  }, [user, companyId, companyIdResolved])

  useEffect(() => {
    if (!data || loading || !dbReady) return
    if (previewUserId) return
    if (!companyId) return
    setSaveStatus('unsaved')
    const t = setTimeout(async () => {
      setSaveStatus('saving')
      await saveScorecard(companyId, data)
      setSaveStatus('saved')
    }, 2000)
    return () => clearTimeout(t)
  }, [data]) // eslint-disable-line

  function updateData(fn) {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev))
      fn(next)
      return next
    })
  }

  function addMetric() {
    updateData(d => {
      d.metrics.push({
        id: nid++, name: 'New metric', owner: '', goal: '',
        cadence: 'weekly', direction: 'higher', entries: {}
      })
    })
  }

  function deleteMetric(id) {
    updateData(d => { d.metrics = d.metrics.filter(m => m.id !== id) })
  }

  function updateEntry(metricId, weekKey, field, val) {
    updateData(d => {
      const m = d.metrics.find(m => m.id === metricId)
      if (!m) return
      if (!m.entries[weekKey]) m.entries[weekKey] = { actual: '' }
      m.entries[weekKey][field] = val
    })
  }

  function updateMeta(id, field, val) {
    updateData(d => {
      const m = d.metrics.find(m => m.id === id)
      if (m) m[field] = val
    })
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ fontSize: 14, color: '#888' }}>Loading scorecard…</div>
    </div>
  )

  const WEEKS = getWeeksForQuarter(quarter)
  const weekKeys = WEEKS.map(w => w.key)
  const thisWeekKey = weekKeys[weekKeys.length - 1]

  // Build per-metric derived data once
  const metricViews = (data?.metrics || []).map(m => {
    const latest = getLatestEntryWithin(m, weekKeys)
    const status = latest ? deriveStatus(m.goal, latest.actual, m.direction) : 'empty'
    const numericEntries = getNumericEntriesSorted(m)
    const sparkValues = numericEntries.slice(-13).map(e => e.actual)
    const trailingMisses = countTrailingMisses(m, weekKeys)
    const goalParseable = !isNaN(parseNumeric(m.goal))
    return { metric: m, latest, status, sparkValues, trailingMisses, goalParseable }
  })

  const reds = metricViews.filter(v => v.status === 'red')
  const needsGoals = metricViews.filter(v => !v.goalParseable)

  function startEditingLatest(metric, currentValue) {
    setEditingLatest(metric.id)
    setLatestDraft(currentValue ?? '')
  }

  function commitLatest(metricId) {
    if (!thisWeekKey) { setEditingLatest(null); return }
    updateEntry(metricId, thisWeekKey, 'actual', latestDraft)
    setEditingLatest(null)
    setLatestDraft('')
  }

  return (
    <div style={{ padding: isMobile ? '20px 14px' : 32, maxWidth: 1280, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', marginBottom: isMobile ? 18 : 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <h1 style={{ fontSize: isMobile ? 20 : 22, fontWeight: 600, color: '#2C2C2A', marginBottom: 4 }}>Scorecard</h1>
          <div style={{ fontSize: 12, color: '#888780' }}>What needs your attention this week</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexWrap: 'wrap', flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: saveStatus === 'saved' ? '#3B6D11' : saveStatus === 'saving' ? '#854F0B' : '#888', whiteSpace: 'nowrap' }}>
            {saveStatus === 'saved' && '✓ Saved'}
            {saveStatus === 'saving' && '↑ Saving…'}
            {saveStatus === 'unsaved' && '● Unsaved'}
          </span>
          {!dbReady && !isMobile && (
            <span style={{ fontSize: 11, color: '#991B1B', background: '#FEF2F2', padding: '4px 10px', borderRadius: 4, border: '1px solid #FCA5A5' }}>
              DB setup needed — see instructions below
            </span>
          )}
          <select
            value={quarter}
            onChange={e => setQuarter(e.target.value)}
            style={{ fontSize: 13, fontWeight: 500, border: '1px solid #D0CDC7', borderRadius: 6, padding: '6px 12px', background: '#fff', color: '#2C2C2A', cursor: 'pointer' }}
          >
            {getQuarterOptions().map(q => <option key={q} value={q}>{q}</option>)}
          </select>
          <button onClick={addMetric} style={{
            fontSize: 12, padding: '7px 14px', borderRadius: 7,
            border: '1px solid #3B6D11', background: 'transparent',
            color: '#3B6D11', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap'
          }}>{isMobile ? '+ Add' : '+ Add metric'}</button>
        </div>
      </div>

      {/* DB-not-ready banner (preserved) */}
      {!dbReady && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '14px 18px', marginBottom: 24, fontSize: 13, color: '#991B1B', lineHeight: 1.6 }}>
          <strong>One more setup step:</strong> The scorecard needs a database table. Go to Supabase → SQL Editor and run:<br />
          <code style={{ display: 'block', background: '#fff', padding: '10px', marginTop: 8, borderRadius: 4, fontSize: 12, color: '#333', whiteSpace: 'pre-wrap' }}>{`CREATE TABLE scorecards (
  id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL UNIQUE,
  scorecard_data JSONB NOT NULL DEFAULT '{}',
  updated_at TIMESTAMPTZ DEFAULT NOW()
);
ALTER TABLE scorecards ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Users can read own scorecard" ON scorecards FOR SELECT USING (auth.uid() = user_id);
CREATE POLICY "Users can insert own scorecard" ON scorecards FOR INSERT WITH CHECK (auth.uid() = user_id);
CREATE POLICY "Users can update own scorecard" ON scorecards FOR UPDATE USING (auth.uid() = user_id);`}</code>
        </div>
      )}

      {/* THIS WEEK'S FOCUS */}
      {reds.length > 0 && (
        <div style={{
          background: '#fff', border: '1px solid #EBE7DE', borderRadius: 10,
          borderLeft: '4px solid #B04545', padding: '16px 20px', marginBottom: 20
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#2C2C2A', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#B04545' }}>●</span>
            This week's focus — {reds.length} metric{reds.length === 1 ? '' : 's'} off track
          </div>
          <div style={{ fontSize: 12, color: '#888780', marginBottom: 14 }}>
            Bring these to The Weekly. Everything else is on rhythm.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {reds.map(({ metric, latest, trailingMisses }) => (
              <div key={metric.id} style={{
                border: '1px solid #EBE7DE', borderRadius: 8, padding: '12px 14px',
                background: '#FBF7EE'
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2A', marginBottom: 4 }}>
                  {metric.name}
                </div>
                <div style={{ fontSize: 11, color: '#888780', marginBottom: 6 }}>
                  {metric.owner ? `Owner: ${metric.owner} · ` : ''}{latest?.key ? `Week of ${formatWeekLabel(latest.key)}` : 'No entry'}
                </div>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#B04545' }}>
                  {latest?.actual ?? '—'}
                </div>
                <div style={{ fontSize: 11, color: '#888780', marginTop: 2 }}>
                  Goal: {metric.goal || '—'}
                  {trailingMisses >= 2 ? ` · ${trailingMisses}${ord(trailingMisses)} consecutive miss` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MAIN — card list on mobile, table on desktop */}
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {metricViews.map(({ metric, latest, status, sparkValues, goalParseable }) => {
            const s = STATUS[status]
            const expanded = expandedId === metric.id
            const isEditingLatest = editingLatest === metric.id
            return (
              <div key={metric.id} style={{ background: '#fff', border: '1px solid #EBE7DE', borderLeft: `3px solid ${s.stroke}`, borderRadius: 10, padding: '14px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Top row: name + status pill */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                    {editingMeta === `${metric.id}-name` ? (
                      <input autoFocus value={metric.name}
                        onChange={e => updateMeta(metric.id, 'name', e.target.value)}
                        onBlur={() => setEditingMeta(null)}
                        onKeyDown={e => { if (e.key === 'Enter') setEditingMeta(null) }}
                        style={{ ...cellInputStyle, width: '100%', fontSize: 16, fontWeight: 600 }} />
                    ) : (
                      <div onClick={() => setEditingMeta(`${metric.id}-name`)} style={{ cursor: 'text', fontWeight: 600, color: '#2C2C2A', fontSize: 16, lineHeight: 1.3 }}>
                        {metric.name}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#888780', marginTop: 4 }}>
                      {metric.owner ? (
                        editingMeta === `${metric.id}-owner` ? (
                          <input autoFocus value={metric.owner}
                            onChange={e => updateMeta(metric.id, 'owner', e.target.value)}
                            onBlur={() => setEditingMeta(null)}
                            onKeyDown={e => { if (e.key === 'Enter') setEditingMeta(null) }}
                            style={{ ...cellInputStyle, width: 120, fontSize: 12 }} placeholder="Name" />
                        ) : (
                          <span onClick={() => setEditingMeta(`${metric.id}-owner`)} style={{ cursor: 'text', color: '#4A4840', background: '#F5EFE0', padding: '2px 8px', borderRadius: 10 }}>{metric.owner}</span>
                        )
                      ) : (
                        <span onClick={() => setEditingMeta(`${metric.id}-owner`)} style={{ cursor: 'text', color: '#aaa', fontStyle: 'italic' }}>+ Owner</span>
                      )}
                      <span style={{ margin: '0 6px', color: '#ccc' }}>·</span>
                      <select
                        value={metric.cadence}
                        onChange={e => updateMeta(metric.id, 'cadence', e.target.value)}
                        style={{ fontSize: 11, border: 'none', background: 'transparent', color: '#888780', cursor: 'pointer', padding: 0 }}
                      >
                        <option value="weekly">weekly</option>
                        <option value="monthly">monthly</option>
                      </select>
                      <span style={{ margin: '0 4px', color: '#ccc' }}>·</span>
                      <select
                        value={metric.direction || 'higher'}
                        onChange={e => updateMeta(metric.id, 'direction', e.target.value)}
                        style={{ fontSize: 11, border: 'none', background: 'transparent', color: '#888780', cursor: 'pointer', padding: 0 }}
                      >
                        <option value="higher">higher = better</option>
                        <option value="lower">lower = better</option>
                      </select>
                    </div>
                  </div>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500,
                    background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                    flexShrink: 0
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color }} />
                    {s.label}
                  </span>
                </div>

                {/* Middle: latest value + goal + sparkline */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ flex: '0 1 auto' }}>
                    <div style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em', color: '#888780', marginBottom: 2 }}>Latest</div>
                    {isEditingLatest ? (
                      <input
                        autoFocus
                        value={latestDraft}
                        onChange={e => setLatestDraft(e.target.value)}
                        onBlur={() => commitLatest(metric.id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') commitLatest(metric.id)
                          if (e.key === 'Escape') { setEditingLatest(null); setLatestDraft('') }
                        }}
                        style={{
                          width: 110, fontSize: 22, fontWeight: 700,
                          border: '1px solid #3B6D11', borderRadius: 6,
                          padding: '4px 8px', outline: 'none'
                        }}
                        placeholder="—"
                      />
                    ) : (
                      <div onClick={() => startEditingLatest(metric, latest?.actual)}
                        style={{
                          cursor: 'text', fontSize: 24, fontWeight: 700, lineHeight: 1.1,
                          color: status === 'red' ? '#B04545' : status === 'yellow' ? '#854D0E' : status === 'green' ? '#166534' : '#bbb'
                        }}>
                        {latest?.actual ?? '—'}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: '0 1 auto' }}>
                    <div style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em', color: '#888780', marginBottom: 2 }}>Goal</div>
                    {editingMeta === `${metric.id}-goal` ? (
                      <input autoFocus value={metric.goal}
                        onChange={e => updateMeta(metric.id, 'goal', e.target.value)}
                        onBlur={() => setEditingMeta(null)}
                        onKeyDown={e => { if (e.key === 'Enter') setEditingMeta(null) }}
                        style={{ ...cellInputStyle, width: 110, fontSize: 16, fontWeight: 500 }} placeholder="50k, 60%" />
                    ) : (
                      <div onClick={() => setEditingMeta(`${metric.id}-goal`)} style={{
                        cursor: 'text', fontSize: 18, fontWeight: 500, lineHeight: 1.1,
                        color: metric.goal ? '#2C2C2A' : '#bbb', fontStyle: metric.goal ? 'normal' : 'italic'
                      }}>
                        {metric.goal || 'Set'}
                      </div>
                    )}
                  </div>
                  <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
                    <Sparkline values={sparkValues} status={status} />
                  </div>
                </div>

                {!goalParseable && metric.goal && (
                  <div style={{ fontSize: 10, color: '#854D0E', background: '#FEFCE8', padding: '4px 10px', borderRadius: 10, border: '1px solid #FDE047' }}>
                    ⚠ Goal isn't a number — status can't be derived
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 4 }}>
                  <button onClick={() => setExpandedId(expanded ? null : metric.id)} style={{
                    fontSize: 12, padding: '7px 14px', borderRadius: 6,
                    border: '1px solid #D0CDC7', background: '#fff', color: '#4A4840',
                    cursor: 'pointer', flex: '1 1 auto'
                  }}>
                    {expanded ? 'Hide weekly entries' : 'View weekly entries'}
                  </button>
                  <button onClick={() => deleteMetric(metric.id)} style={{
                    border: '1px solid #EBE7DE', background: 'none', cursor: 'pointer',
                    color: '#B04545', fontSize: 16, lineHeight: 1, padding: '6px 12px',
                    borderRadius: 6
                  }} title="Delete metric">×</button>
                </div>

                {/* Expanded weekly entries */}
                {expanded && (
                  <div style={{ borderTop: '1px solid #EBE7DE', paddingTop: 12, marginTop: 4 }}>
                    <div style={{ fontSize: 10, color: '#888780', marginBottom: 8, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                      Entries · {metric.cadence === 'monthly' ? 'enter on month-end Mondays' : 'weekly'}
                    </div>
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr>
                            {WEEKS.map(w => (
                              <th key={w.key} style={{
                                padding: '6px 8px', fontSize: 10, fontWeight: 500,
                                color: w.key === thisWeekKey ? '#3B6D11' : '#888780',
                                minWidth: 56, textAlign: 'center'
                              }}>{w.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {WEEKS.map(w => {
                              const e = metric.entries[w.key] || {}
                              const cellStatus = deriveStatus(metric.goal, e.actual, metric.direction)
                              const cellS = STATUS[cellStatus]
                              const isThisWeek = w.key === thisWeekKey
                              return (
                                <td key={w.key} style={{ padding: '4px 4px', textAlign: 'center', background: isThisWeek ? '#F0FDF4' : undefined }}>
                                  <input
                                    value={e.actual || ''}
                                    onChange={ev => updateEntry(metric.id, w.key, 'actual', ev.target.value)}
                                    placeholder="—"
                                    style={{
                                      width: 52, fontSize: 13, textAlign: 'center',
                                      padding: '6px 4px',
                                      border: `1px solid ${cellS.border}`,
                                      borderRadius: 4, outline: 'none',
                                      background: cellStatus === 'empty' ? '#fff' : cellS.bg,
                                      color: cellS.color
                                    }}
                                  />
                                </td>
                              )
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
          {metricViews.length === 0 && (
            <div style={{ fontSize: 13, color: '#aaa', padding: '24px 0', textAlign: 'center', background: '#F9F7F4', borderRadius: 10, border: '1px dashed #D0CDC7' }}>
              No metrics yet — tap "+ Add" to create one
            </div>
          )}
        </div>
      ) : (
      <div style={{
        background: '#fff', border: '1px solid #EBE7DE', borderRadius: 10,
        overflow: 'hidden', marginBottom: 20
      }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#1E3320' }}>
              <th style={{ ...thStyle, width: '30%', textAlign: 'left' }}>Metric</th>
              <th style={{ ...thStyle, width: '14%', textAlign: 'left' }}>Owner</th>
              <th style={{ ...thStyle, width: '14%', textAlign: 'right' }}>Latest</th>
              <th style={{ ...thStyle, width: '16%', textAlign: 'center' }}>Trend</th>
              <th style={{ ...thStyle, width: '14%', textAlign: 'center' }}>Status</th>
              <th style={{ ...thStyle, width: '12%', textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {metricViews.map(({ metric, latest, status, sparkValues, goalParseable }, idx) => {
              const s = STATUS[status]
              const expanded = expandedId === metric.id
              const isEditingLatest = editingLatest === metric.id
              return (
                <Fragment key={metric.id}>
                  <tr style={{ borderBottom: '1px solid #EBE7DE', background: idx % 2 === 0 ? '#fff' : '#FCFAF6' }}>
                    {/* Metric name + cadence */}
                    <td style={tdStyle}>
                      {editingMeta === `${metric.id}-name` ? (
                        <input autoFocus value={metric.name}
                          onChange={e => updateMeta(metric.id, 'name', e.target.value)}
                          onBlur={() => setEditingMeta(null)}
                          onKeyDown={e => { if (e.key === 'Enter') setEditingMeta(null) }}
                          style={cellInputStyle} />
                      ) : (
                        <div onClick={() => setEditingMeta(`${metric.id}-name`)} style={{ cursor: 'text', fontWeight: 600, color: '#2C2C2A' }}>
                          {metric.name}
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: '#888780', marginTop: 3, textTransform: 'capitalize' }}>
                        <select
                          value={metric.cadence}
                          onChange={e => updateMeta(metric.id, 'cadence', e.target.value)}
                          style={{ fontSize: 10, border: 'none', background: 'transparent', color: '#888780', cursor: 'pointer', padding: 0 }}
                        >
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                        {' · '}
                        <select
                          value={metric.direction || 'higher'}
                          onChange={e => updateMeta(metric.id, 'direction', e.target.value)}
                          style={{ fontSize: 10, border: 'none', background: 'transparent', color: '#888780', cursor: 'pointer', padding: 0 }}
                        >
                          <option value="higher">higher is better</option>
                          <option value="lower">lower is better</option>
                        </select>
                      </div>
                    </td>

                    {/* Owner */}
                    <td style={tdStyle}>
                      {editingMeta === `${metric.id}-owner` ? (
                        <input autoFocus value={metric.owner}
                          onChange={e => updateMeta(metric.id, 'owner', e.target.value)}
                          onBlur={() => setEditingMeta(null)}
                          onKeyDown={e => { if (e.key === 'Enter') setEditingMeta(null) }}
                          style={cellInputStyle} placeholder="Name" />
                      ) : (
                        <span onClick={() => setEditingMeta(`${metric.id}-owner`)} style={{
                          cursor: 'text', display: 'inline-block',
                          padding: '3px 10px', borderRadius: 12, fontSize: 12,
                          background: metric.owner ? '#F5EFE0' : 'transparent',
                          color: metric.owner ? '#4A4840' : '#aaa',
                          fontStyle: metric.owner ? 'normal' : 'italic'
                        }}>
                          {metric.owner || 'Add owner'}
                        </span>
                      )}
                    </td>

                    {/* Latest (inline editable) */}
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {isEditingLatest ? (
                        <input
                          autoFocus
                          value={latestDraft}
                          onChange={e => setLatestDraft(e.target.value)}
                          onBlur={() => commitLatest(metric.id)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') commitLatest(metric.id)
                            if (e.key === 'Escape') { setEditingLatest(null); setLatestDraft('') }
                          }}
                          style={{
                            width: 90, fontSize: 15, fontWeight: 600,
                            border: '1px solid #3B6D11', borderRadius: 4,
                            padding: '4px 6px', textAlign: 'right', outline: 'none'
                          }}
                          placeholder="—"
                        />
                      ) : (
                        <div onClick={() => startEditingLatest(metric, latest?.actual)}
                          style={{ cursor: 'text', display: 'inline-block' }}>
                          <div style={{
                            fontSize: 16, fontWeight: 600,
                            color: status === 'red' ? '#B04545' : status === 'yellow' ? '#854D0E' : status === 'green' ? '#166534' : '#bbb'
                          }}>
                            {latest?.actual ?? '—'}
                          </div>
                          <div style={{ fontSize: 10, color: '#888780', marginTop: 1 }}>
                            {metric.goal ? `goal ${metric.goal}` : 'no goal'}
                          </div>
                        </div>
                      )}
                    </td>

                    {/* Trend */}
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <div style={{ display: 'inline-block' }}>
                        <Sparkline values={sparkValues} status={status} />
                      </div>
                    </td>

                    {/* Status */}
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500,
                        background: s.bg, color: s.color, border: `1px solid ${s.border}`
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color }} />
                        {s.label}
                      </span>
                    </td>

                    {/* Actions */}
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <button onClick={() => setExpandedId(expanded ? null : metric.id)} style={{
                        fontSize: 11, padding: '4px 10px', borderRadius: 5,
                        border: '1px solid #D0CDC7', background: '#fff', color: '#4A4840',
                        cursor: 'pointer', marginRight: 6
                      }}>
                        {expanded ? 'Hide entries' : 'View entries'}
                      </button>
                      <button onClick={() => deleteMetric(metric.id)} style={{
                        border: 'none', background: 'none', cursor: 'pointer',
                        color: '#ccc', fontSize: 16, lineHeight: 1
                      }} title="Delete metric">×</button>
                    </td>
                  </tr>

                  {/* EXPANDED ENTRIES */}
                  {expanded && (
                    <tr style={{ background: '#FCFAF6' }}>
                      <td colSpan={6} style={{ padding: '14px 20px 18px 20px', borderBottom: '1px solid #EBE7DE' }}>
                        <div style={{ fontSize: 11, color: '#888780', marginBottom: 8, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                          Entries · {metric.cadence === 'monthly' ? 'enter on month-ending Mondays' : 'weekly'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                          <div style={{ fontSize: 11, color: '#888780', whiteSpace: 'nowrap', paddingTop: 6 }}>Goal</div>
                          {editingMeta === `${metric.id}-goal` ? (
                            <input autoFocus value={metric.goal}
                              onChange={e => updateMeta(metric.id, 'goal', e.target.value)}
                              onBlur={() => setEditingMeta(null)}
                              onKeyDown={e => { if (e.key === 'Enter') setEditingMeta(null) }}
                              style={{ ...cellInputStyle, width: 140 }} placeholder="e.g. 50k, 60%, $12,000" />
                          ) : (
                            <div onClick={() => setEditingMeta(`${metric.id}-goal`)} style={{
                              cursor: 'text', fontSize: 13, color: metric.goal ? '#2C2C2A' : '#aaa',
                              padding: '4px 8px', border: '1px dashed #D0CDC7', borderRadius: 4
                            }}>
                              {metric.goal || 'Set goal'}
                            </div>
                          )}
                          {!goalParseable && metric.goal && (
                            <span style={{ fontSize: 10, color: '#854D0E', background: '#FEFCE8', padding: '3px 8px', borderRadius: 10, border: '1px solid #FDE047' }}>
                              ⚠ goal isn't a number — status can't be derived
                            </span>
                          )}
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr>
                                {WEEKS.map(w => (
                                  <th key={w.key} style={{
                                    padding: '6px 8px', fontSize: 10, fontWeight: 500,
                                    color: w.key === thisWeekKey ? '#3B6D11' : '#888780',
                                    minWidth: 60, textAlign: 'center'
                                  }}>{w.label}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                {WEEKS.map(w => {
                                  const e = metric.entries[w.key] || {}
                                  const cellStatus = deriveStatus(metric.goal, e.actual, metric.direction)
                                  const cellS = STATUS[cellStatus]
                                  const isThisWeek = w.key === thisWeekKey
                                  return (
                                    <td key={w.key} style={{ padding: '4px 4px', textAlign: 'center', background: isThisWeek ? '#F0FDF4' : undefined }}>
                                      <input
                                        value={e.actual || ''}
                                        onChange={ev => updateEntry(metric.id, w.key, 'actual', ev.target.value)}
                                        placeholder="—"
                                        style={{
                                          width: 56, fontSize: 12, textAlign: 'center',
                                          padding: '4px 4px',
                                          border: `1px solid ${cellS.border}`,
                                          borderRadius: 4, outline: 'none',
                                          background: cellStatus === 'empty' ? '#fff' : cellS.bg,
                                          color: cellS.color
                                        }}
                                      />
                                    </td>
                                  )
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
        </div>
      </div>
      )}

      {/* NEEDS GOALS */}
      {needsGoals.length > 0 && (
        <div style={{
          background: '#FBF7EE', border: '1px dashed #D0CDC7', borderRadius: 10,
          padding: '14px 18px', marginBottom: 20
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2A', marginBottom: 4 }}>
            ⚠ {needsGoals.length} metric{needsGoals.length === 1 ? '' : 's'} need goals before status can be calculated
          </div>
          <div style={{ fontSize: 12, color: '#888780', marginBottom: 10 }}>
            A scorecard without goals is just a list. Set a numeric goal so on-track / off-track is meaningful.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {needsGoals.map(({ metric }) => (
              <button key={metric.id} onClick={() => { setExpandedId(metric.id); setEditingMeta(`${metric.id}-goal`) }} style={{
                background: '#fff', border: '1px solid #D0CDC7', borderRadius: 14,
                padding: '5px 12px', fontSize: 12, cursor: 'pointer', display: 'inline-flex', gap: 8, alignItems: 'center'
              }}>
                {metric.name}
                <span style={{ color: '#B04545', fontWeight: 600 }}>→ Set goal</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {(data?.metrics || []).length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#888780', fontSize: 14 }}>
          No metrics yet. Click "+ Add metric" to get started.
        </div>
      )}

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {['green', 'yellow', 'red', 'empty'].map(key => {
          const s = STATUS[key]
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#888780' }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: s.bg, border: `1px solid ${s.border}` }} />
              {s.label}
            </div>
          )
        })}
        <div style={{ fontSize: 11, color: '#aaa', marginLeft: 'auto' }}>
          Click any value to edit · Status auto-calculated from goal · Changes save automatically
        </div>
      </div>
    </div>
  )
}

// ---------- small utils ----------
function formatWeekLabel(isoKey) {
  if (!isoKey) return ''
  const d = new Date(isoKey + 'T00:00:00')
  return `${d.getMonth() + 1}/${d.getDate()}`
}

function ord(n) {
  if (n % 100 >= 11 && n % 100 <= 13) return 'th'
  switch (n % 10) {
    case 1: return 'st'
    case 2: return 'nd'
    case 3: return 'rd'
    default: return 'th'
  }
}

const thStyle = {
  padding: '12px 14px', fontSize: 11, fontWeight: 500,
  letterSpacing: '.06em', textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap'
}

const tdStyle = {
  padding: '12px 14px', verticalAlign: 'middle'
}

const cellInputStyle = {
  width: '100%', fontSize: 13, border: '1px solid #3B6D11',
  borderRadius: 4, padding: '4px 6px', outline: 'none', background: '#fff'
}
